import { use } from 'react';
import { sessionState } from '@/app/sessionState';
import { config as appConfig } from '@/app/config';
import { useLoading, LoadingService } from '@/app/LoadingProvider';

export const defaultAPIFetchOptions = {
  mode: 'cors',
  headers: {
    'X-Requested-By': 'adenin Artifacts',
    'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
  },
};

/**
 *  
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options
 * @param {object} config - Additional config like showLoading, signal
 * @returns {Promise} - Promise with the response data
 */
export const createFetchRequest = (url, options = {}, config = {}) => {
  var { showLoading = true } = config;

  let optionsDefaults = {
    // add config in the case needed in the future credentials: 'include',
  }

  if (options.method !== 'GET') {
    optionsDefaults.headers = {
      'Content-Type': 'application/json'
    }
  };

  // Merge options, with custom options overriding defaults
  options = {
    ...optionsDefaults,
    ...options,
    // Special handling for nested headers object
    headers: {
      ...optionsDefaults.headers,
      ...(options.headers || {})
    }
  };

  // Prepare headers
  const headers = new Headers(options.headers || {});

  // Add authorization header if not present
  if (!headers.has('Authorization') && !url.includes('adenin-catalog.netlify.app')) {
    const token = sessionState.token;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  } 

  // Include cookies and x-subject header when sub has a value
  if (sessionState.sub) {
    options.credentials = 'include';
    headers.set('x-subject', sessionState.sub);
  }

  // Add content-type for non-GET requests if not present
  if (!headers.has('Content-Type') && options.method !== 'GET') {
    headers.set('Content-Type', 'application/json');
  }

  // Create the fetch promise
  return (async () => {
    // Handle loading state through LoadingService
    let loaderId = null;
    if (showLoading) {
      loaderId = LoadingService.startLoading();
    }

    // Convert body to JSON string for non-GET requests
    const fetchOptions = {
      ...options,
      headers
    };

    if (options.body && typeof options.body === 'object' && options.method !== 'GET') {
      fetchOptions.body = JSON.stringify(options.body);
    }

    try {
      // Check if already cancelled
      if (options.signal?.aborted) {
        throw new DOMException('Request was cancelled', 'AbortError');
      }

      let { url: fetchUrl, isAPI } = mapAPIUrl(url);
      const response = await fetch(fetchUrl, fetchOptions);

      // Handle non-2xx responses
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || `Request failed with status ${response.status}`);
        error.name = 'ApiError';
        error.status = response.status;
        error.data = errorData;
        throw error;
      }

      // Parse JSON for JSON content type responses
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return response.json();
      }

      return response;
    } catch (error) {
      // Handle cancellation gracefully
      if (error.name === 'AbortError') {
        console.log('Request was cancelled');
      }
      throw error;
    } finally {
      // Stop loading when done
      if (showLoading && loaderId) {
        LoadingService.stopLoading(loaderId);
      }
    }
  })();
};

/**
 * Suspendable fetch hook with integrated loading state
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options
 * @param {object} config - Additional config like caching
 * @returns {any} - The response data
 */
export function useFetch(url, options = {}, config = {}) {
  // Get the loading state mechanisms
  const { startLoading, stopLoading } = useLoading();
  let loaderId = null;

  // Create and execute the fetch request
  const promise = (async () => {
    // Start loading indicator
    loaderId = startLoading();

    try {
      // Execute the actual fetch without showing global loading state
      return await createFetchRequest(url, options, { ...config, showLoading: false });
    } finally {
      // Always stop the loading indicator when done
      if (loaderId) {
        stopLoading(loaderId);
      }
    }
  })();

  return use(promise);
}

/**
 * Converts an object of parameters to a URL query string.
 * Handles ${param} placeholders in the URL path and query string.
 * Date objects are converted to ISO format in UTC.
 * 
 * @param {string} url - The base URL, can contain ${param} placeholder
 * @param {Object} params - The parameters object to convert
 * @returns {string} The processed URL with parameters
 */
export function paramsToUrl(url, params) {
  // Create a copy of params that we can modify
  const paramsCopy = { ...params };
  let result = url;

  // Helper function to format parameter values consistently
  const formatValue = (value) => {
    if (value === null || value === undefined) {
      return null;
    }

    if (value instanceof Date) {
      return value.toISOString();
    }

    return String(value);
  };

  // Find all placeholder matches
  const regex = /{([^}]+)}/g;
  const placeholderMatches = [...url.matchAll(regex)];

  // Process each placeholder match
  for (const match of placeholderMatches) {
    const fullMatch = match[0];     // e.g. ${id}
    const paramName = match[1];     // e.g. id

    // Format the value using the helper
    const value = paramsCopy[paramName];
    const formattedValue = formatValue(value);

    // Remove the used parameter
    delete paramsCopy[paramName];

    // Skip if null/undefined
    if (formattedValue === null) {
      result = result.replace(fullMatch, '');
      continue;
    }

    // Determine correct encoding
    const isInPath = url.indexOf('?') === -1 || url.indexOf(fullMatch) < url.indexOf('?');
    let encodedValue;

    if (isInPath) {
      // In path: encode but preserve certain characters
      encodedValue = encodeURIComponent(formattedValue)
        .replace(/%2F/g, '/')
        .replace(/%40/g, '@')
        .replace(/%3A/g, ':')
        .replace(/%24/g, '$')
        .replace(/%2C/g, ',')
        .replace(/%3B/g, ';');
    } else {
      // In query: standard encoding
      encodedValue = encodeURIComponent(formattedValue);
    }

    // Replace the placeholder
    result = result.replace(fullMatch, encodedValue);

  }

  // Add remaining params as query string
  if (Object.keys(paramsCopy).length > 0) {
    const searchParams = new URLSearchParams();

    for (const key in paramsCopy) {
      // Format using same helper
      const formattedValue = formatValue(paramsCopy[key]);

      // Skip null or undefined
      if (formattedValue === null) {
        continue;
      }

      searchParams.append(key, formattedValue);
    }

    const queryString = searchParams.toString();
    if (queryString) {
      result += (result.includes('?') ? '&' : '?') + queryString;
    }
  }

  return result;
}


/*
* creates url and query key for react-query
* @param {string} url - The URL to fetch  
* @param {object} params - The parameters object to convert
*/
export function createQueryKey(url, params) {
  let q = undefined
  let { url: _url, isAPI: _isAPI } = mapAPIUrl(url);

  if (params) {
    q = paramsToUrlQuery(parameters);
    if (q) {
      _url = `${_url}?${q}`;
    }
  }

  return { url: _url, isAPI: _isAPI, queryKey: [url, q] };
}

/**
 *  map relative API URL and and replace https://app.adenin.com/ with current environment API URL
 */
export function mapAPIUrl(url) {

  let result = { url, isAPI: false }

  // Check if URL starts with /api/ and prepend the API URL
  if (url.startsWith('/')) {
    result.url = appConfig.API_URL + url;
    result.isAPI = true;
  }

  if (url.startsWith('https://api.adenin.com/')) {
    result.url = url.replace("https://api.adenin.com", appConfig.API_URL, url);
    result.isAPI = true;
  }

  // Otherwise, return the original URL
  return result;
}

/**
 * Convenience method for GET requests with loading indicator
 */
export function get(url, options = {}, config = {}) {
  return createFetchRequest(url, { ...options, method: 'GET' }, config);
}

/**
 * Convenience method for POST requests with loading indicator
 */
export function post(url, data = {}, options = {}, config = {}) {
  return createFetchRequest(
    url,
    { ...options, method: 'POST', body: data },
    config
  );
}

/**
 * Convenience method for PUT requests with loading indicator
 */
export function put(url, data = {}, options = {}, config = {}) {
  return createFetchRequest(
    url,
    { ...options, method: 'PUT', body: data },
    config
  );
}

/**
 * Convenience method for DELETE requests with loading indicator
 */
export function del(url, options = {}, config = {}) {
  return createFetchRequest(
    url,
    { ...options, method: 'DELETE' },
    config
  );
}